import classnames from 'classnames';
import { replaceAt } from 'common/util';
import _, { isArray, isBoolean, isNull, isNumber, isObject, isString } from 'lodash';
import React, { Component, useState } from 'react';
import DragDropContainer, {
  DragDropContainerType,
  makeCustomDragDropElementWrapper
} from '../DragDropContainer';
import { Checkbox } from '../Forms';
import Input from '../Forms/Input';
import './style.scss';
import { I18n } from 'common/i18n';
import { IOption } from '@tylertech/forge';
import { ForgeMenu, ForgeIconButton, ForgeIcon } from '@tylertech/forge-react';

// Internationalization helpers
const t = (key: string, params?: Record<string, string | number>) =>
  I18n.t(key, { ...(params || {}), scope: 'shared.components.object_editor' });

const restrictedText = I18n.t('shared.dataset_management_ui.metadata_manage.dataset_tab.subtitles.restricted_field');

// Type guards helpers
const isPrimitive = (value: Json): boolean =>
  isString(value) || isNumber(value) || isBoolean(value) || isNull(value);

const isComposite = (value: Json): boolean =>
  isArray(value) || isObject(value);

export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

interface Props<T> {
  id: string;
  thing: T;
  onChange: (value: T | null) => void;
  isRestrictedForUser: boolean;
}

enum Adding {
  Text,
  Number,
  Boolean,
  List,
  Object
}
interface Option {
  text: string;
  value: Adding;
}

function AddChild({ onAdd, isRestrictedForUser }: { onAdd: (a: Json) => void, isRestrictedForUser: boolean }) {
  if (isRestrictedForUser) return null;
  const onSelection = (op: Option | IOption) => {
    if (op.value === Adding.Text) {
      onAdd('');
    }
    if (op.value === Adding.Number) {
      onAdd(0);
    }
    if (op.value === Adding.Boolean) {
      onAdd(true);
    }
    if (op.value === Adding.List) {
      onAdd([]);
    }
    if (op.value === Adding.Object) {
      onAdd({});
    }
  };

  const options = [
    { label: t('add_text'), value: Adding.Text },
    { label: t('add_number'), value: Adding.Number },
    { label: t('add_boolean'), value: Adding.Boolean },
    { label: t('add_list'), value: Adding.List },
    { label: t('add_object'), value: Adding.Object }
  ];
  return (
    (<ForgeMenu
      options={options}
      on-forge-menu-select={({ detail }: CustomEvent) => onSelection(detail)}
    >
      <ForgeIconButton aria-label={t('add_value')} data-testid="object-editor-add-value-button">
        <ForgeIcon name="add" />
      </ForgeIconButton>
    </ForgeMenu>)
  );
}

function AddKVPair({ onAdd, isRestrictedForUser }: { onAdd: (k: string, o: Json) => void, isRestrictedForUser: boolean }) {
  const [name, setName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  if (isRestrictedForUser) return null;

  const onClick = () => {
    setIsAdding(true);
  };
  if (isAdding) {
    return (
      (<div className="add-key-value">
        <Input
          containerClassName="add-key-value-name"
          placeholder={t('property_name')}
          label={t('add_property')}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <AddChild
          onAdd={(value) => {
            onAdd(name, value);
            setIsAdding(false);
          }}
          isRestrictedForUser={isRestrictedForUser}
        />
        <ForgeIconButton
          aria-label={t('nullify')}
          onClick={() => setIsAdding(false)}
          data-testid="object-editor-nullify-button">
          <ForgeIcon name="close" />
        </ForgeIconButton>
      </div>)
    );
  }
  return (
    (<div className="add-kv forged">
      <ForgeIconButton
        aria-label={t('add_key')}
        onClick={onClick}
        data-testid="object-editor-add-button">
        <ForgeIcon name="add" />
      </ForgeIconButton>
    </div>)
  );
}

function RemoveChild({ onRemove, label, isRestrictedForUser }: { onRemove: () => void; label: string, isRestrictedForUser: boolean }) {
  if (isRestrictedForUser) return null;
  return (
    (<ForgeIconButton
      dense={true}
      data-testid="object-editor-remove-child"
      aria-label={label}
      onClick={onRemove}>
      <ForgeIcon name="trash_can" />
    </ForgeIconButton>)
  );
}

function Nullify({ onNullify, isRestrictedForUser }: { onNullify: () => void, isRestrictedForUser: boolean }) {
  if (isRestrictedForUser) return null;
  return (
    (<ForgeIconButton
      dense={true}
      aria-label={t('nullify')}
      onClick={onNullify}
      data-testid="object-editor-nullify-close">
      <ForgeIcon name="close" />
    </ForgeIconButton>)
  );
}

function EmptyComposite({ label, onNullify, isRestrictedForUser }: { label: string; onNullify: () => void, isRestrictedForUser: boolean }) {
  return (
    <div className="empty-composite">
      {label}
      <Nullify onNullify={onNullify} isRestrictedForUser={isRestrictedForUser} />
    </div>
  );
}

function ArrayEditor(props: Props<Json[]>) {
  const { id, thing, isRestrictedForUser, onChange } = props;

  // Render array item
  const renderArrayItem = (subThing: Json, index: number) => {
    const subKey = `${id}.${index}`;
    return (
      <div className="json-array-item" key={index}>
        <ObjectEditor
          key={subKey}
          thing={subThing}
          id={subKey}
          onChange={(newSubThing) => onChange(replaceAt(thing, newSubThing, index))}
          isRestrictedForUser={isRestrictedForUser}
        />
        <RemoveChild
          label={t('remove_index', { index })}
          onRemove={() => onChange(thing.filter((_unused, i) => i !== index))}
          isRestrictedForUser={isRestrictedForUser}
        />
      </div>
    );
  };

  // Create wrapped elements with proper value property for dnd-kit
  const createDragDropElements = () => {
    return thing.map((subThing, index) => ({
      id: `${id}.${index}`,
      element: renderArrayItem(subThing, index),
      dragAndDroppable: !isRestrictedForUser,
      value: subThing // This ensures the actual data value is preserved
    }));
  };

  return (
    <div className="json-array-editor">
      {props.thing.length > 0 ? (
        <DragDropContainer
          type={DragDropContainerType.LIST}
          items={thing}
          onDrop={(newItems) => {
            // Handle both react-dnd and dnd-kit formats
            // If we receive objects with a 'value' property, extract just the values
            const cleanItems = newItems.map((item: any) => {
              // If item has a 'value' property, it's from dnd-kit
              if (item && typeof item === 'object' && 'value' in item) {
                return item.value;
              }
              // If it's already a plain value, use it directly
              return item;
            });
            onChange(cleanItems);
          }}
          iconName="drag_indicator"
          childElements={createDragDropElements()}
        />
      ) : (
        <EmptyComposite label={t('empty_list')} onNullify={() => onChange(null)} isRestrictedForUser={isRestrictedForUser} />
      )}
      <div className={'add-child forged'}>
        <AddChild onAdd={(newSubThing) => props.onChange([...thing, newSubThing])} isRestrictedForUser={isRestrictedForUser} />
      </div>
    </div>
  );
}

function StringEditor({ id, thing, onChange, isRestrictedForUser }: Props<string>) {
  return (
    <div className="json-string-editor">
      <TextField id={id} onChange={onChange} value={thing} isRestrictedForUser={isRestrictedForUser} />
      <Nullify onNullify={() => onChange(null)} isRestrictedForUser={isRestrictedForUser} />
    </div>
  );
}

function NumberEditor({ id, thing, onChange, isRestrictedForUser }: Props<number>) {
  const [error, setError] = useState<string | null>(null);
  const [current, setValue] = useState<string | null>(thing.toString());
  const onChangeTextField = (value: string) => {
    setError(null);
    setValue(value);

    // yes, this lops off trailing characters. oh well.
    const int = parseInt(value);
    const float = parseFloat(value);

    if (!isNaN(int)) {
      return onChange(int);
    }
    if (!isNaN(float)) {
      return onChange(float);
    }

    setError(t('invalid_number'));
  };

  return (
    <div className="json-number-editor">
      {error ? <label>{error}</label> : null}
      <TextField id={id} onChange={onChangeTextField} value={current} isRestrictedForUser={isRestrictedForUser} />
      <Nullify onNullify={() => onChange(null)} isRestrictedForUser={isRestrictedForUser} />
    </div>
  );
}

function BooleanEditor({ thing, id, onChange, isRestrictedForUser }: Props<boolean>) {
  return (
    <div className="json-boolean-editor">
      <Checkbox disabled={isRestrictedForUser} label="" id={id} checked={thing} onChange={(e) => onChange(e.currentTarget.checked)} />
      <Nullify onNullify={() => onChange(null)} isRestrictedForUser={isRestrictedForUser} />
    </div>
  );
}

function NullEditor({ onChange, isRestrictedForUser }: Props<Json>) {
  return (
    <div className="json-null-editor">
      <span className="text-muted">{t('null_value')}</span>
      <AddChild onAdd={onChange} isRestrictedForUser={isRestrictedForUser} />
    </div>
  );
}

// Object key-value pair renderer
function ObjectKeyValuePair({
  objKey,
  value,
  idx,
  id,
  thing,
  onChange,
  isRestrictedForUser
}: {
  objKey: string;
  value: Json;
  idx: number;
  id: string;
  thing: { [key: string]: Json };
  onChange: (value: { [key: string]: Json } | null) => void;
  isRestrictedForUser: boolean;
}) {
  const isPrimitiveValue = isPrimitive(value);
  const isCompositeValue = isComposite(value);
  const subId = `${id}.${idx}`;

  const removeButton = (
    <RemoveChild
      label={t('remove_key', { key: objKey })}
      onRemove={() => onChange(_.omit(thing, objKey))}
      isRestrictedForUser={isRestrictedForUser}
    />
  );

  const onChangeChild = (newChild: Json) => {
    onChange({ ...thing, [objKey]: newChild });
  };

  const onChangeKey = (newKey: string) => {
    onChange({ ..._.omit(thing, objKey), [newKey]: thing[objKey] });
  };

  return (
    <div
      key={subId}
      className={classnames({
        'object-kv': true,
        'object-kv-primitive': isPrimitiveValue,
        'object-kv-composite': isCompositeValue
      })}
    >
      <div className="object-key">
        <TextField
          key={`${subId}-key-editor`}
          id={`${subId}-key-editor`}
          onChange={onChangeKey}
          value={objKey}
          isRestrictedForUser={isRestrictedForUser}
        />
        {isCompositeValue ? <div>{removeButton}</div> : null}
      </div>
      <div className="object-value">
        <ObjectEditor
          thing={value}
          id={subId}
          onChange={onChangeChild}
          isRestrictedForUser={isRestrictedForUser}
        />
      </div>
      {isPrimitiveValue ? <div>{removeButton}</div> : null}
    </div>
  );
}

// Object editor for complex objects
function ComplexObjectEditor({ id, thing, onChange, isRestrictedForUser }: Props<{ [key: string]: Json }>) {
  const sortedKeys = _.sortBy(_.keys(thing), (k) => k.toLowerCase());

  return (
    <div className="json-object-editor">
      {sortedKeys.length === 0 ? (
        <EmptyComposite
          label={t('empty_object')}
          onNullify={() => onChange(null)}
          isRestrictedForUser={isRestrictedForUser}
        />
      ) : null}
      {sortedKeys.map((key, idx) => (
        <ObjectKeyValuePair
          key={`${id}.${idx}`}
          objKey={key}
          value={thing[key]}
          idx={idx}
          id={id}
          thing={thing}
          onChange={onChange}
          isRestrictedForUser={isRestrictedForUser}
        />
      ))}
      <AddKVPair
        onAdd={(newKey, newSubThing) => onChange({ ...thing, [newKey]: newSubThing })}
        isRestrictedForUser={isRestrictedForUser}
      />
    </div>
  );
}

class ObjectEditor extends Component<Props<Json>> {
  render() {
    const { thing, onChange, id, isRestrictedForUser } = this.props;

    // Handle primitive types
    if (isString(thing)) {
      return <StringEditor id={id} thing={thing} onChange={onChange} isRestrictedForUser={isRestrictedForUser} />;
    }
    if (isNumber(thing)) {
      return <NumberEditor id={id} thing={thing} onChange={onChange} isRestrictedForUser={isRestrictedForUser} />;
    }
    if (isBoolean(thing)) {
      return <BooleanEditor id={id} thing={thing} onChange={onChange} isRestrictedForUser={isRestrictedForUser} />;
    }
    if (isNull(thing)) {
      return <NullEditor id={id} thing={thing} onChange={onChange} isRestrictedForUser={isRestrictedForUser} />;
    }

    // Handle composite types
    if (isArray(thing)) {
      return <ArrayEditor id={id} thing={thing} onChange={onChange} isRestrictedForUser={isRestrictedForUser} />;
    }
    if (isObject(thing)) {
      return <ComplexObjectEditor id={id} thing={thing} onChange={onChange} isRestrictedForUser={isRestrictedForUser} />;
    }
  }
}

function TextField({
  id,
  onChange,
  value, isRestrictedForUser
}: {
  id: string;
  onChange: (s: string) => void;
  value: string | null;
  isRestrictedForUser: boolean;
}) {
  return (
    <input
      disabled={isRestrictedForUser}
      id={id}
      className="text-input"
      type="text"
      value={value || ''}
      onChange={(e) => {
        e.preventDefault();
        onChange(e.currentTarget.value);
        return false;
      }}
    />
  );
}

function ObjectEditorWrapper(props: Props<Json> & { displayName?: string }) {
  return (
    <div className="object-editor-wrapper">
      {props.displayName && <label className="object-field-label" aria-label={props.displayName}>
        {props.displayName}
      </label>}
      <ObjectEditor {...props} />
      {props.isRestrictedForUser && <span slot="helper-text" className="object-field-subtitle">{restrictedText}</span>}
    </div>
  );
}

// Note: This component supports both react-dnd (legacy) and dnd-kit (modern) drag-and-drop implementations.
// The implementation is controlled by the 'use_dnd_kit' feature flag or window.useDndKit.
// For react-dnd: The caller must wrap the highest level component with DragDropContext(HTML5Backend)
// to avoid "cannot have two html5 backends at the same time" errors.
// For dnd-kit: No special wrapper is needed as it's handled internally.
export default ObjectEditorWrapper;
